import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import CoverageIndicator from 'components/common/coverage-indicator';
import StatDifference from 'components/common/stat-difference';
import Button from 'components/inputs/button';
import Tooltip from 'components/layout/tooltip';
import { ComparisonTableDataType } from 'data/types';
import { setComparisonTableData } from 'features/ui';
import { forwardRef, MouseEvent } from 'react';
import { formatCoverageScore } from 'utils/coverage-score-format';
import { ductedCoverageTooltipText } from 'utils/ducted-coverage';
import { formatUSD } from 'utils/helpers';
import { formatSeer } from 'utils/seer-format';
import { getCoverageTooltipData } from 'utils/ui';

type UnifiedComparisonTableProps = {
  data: ComparisonTableDataType[];
  mode?: 'systems' | 'quotes';
  showAdvanced?: boolean;
  onToggleAdvanced?: () => void;
};

// Ducted design-load coverage cell (AHA-6796): "—" for null values, the
// design-load explainer as the advanced text (instead of the hours-weighted
// getCoverageTooltipData), and a StatDifference only against another DUCTED
// row — mixing the two coverage metrics in one delta would mislead.
function renderDuctedCoverageContent(
  type: 'heating' | 'cooling',
  data: ComparisonTableDataType,
  comparisonTableData: ComparisonTableDataType | null,
  showAdvanced: boolean
) {
  const coverage = data.ductedCoverage;
  if (!coverage) return null;
  const value =
    type === 'heating'
      ? coverage.heatingCoveragePercent
      : coverage.coolingCoveragePercent;
  const referenceCoverage =
    comparisonTableData && comparisonTableData.identifier !== data.identifier
      ? comparisonTableData.ductedCoverage
      : undefined;
  let referenceValue: number | null = null;
  if (referenceCoverage) {
    referenceValue =
      type === 'heating'
        ? referenceCoverage.heatingCoveragePercent
        : referenceCoverage.coolingCoveragePercent;
  }

  return (
    <div className='flex flex-col items-center gap-2'>
      <div className='flex items-center gap-2'>
        <CoverageIndicator
          type={type}
          ducted={{ value, basis: coverage.basis }}
        />
        {value !== null && referenceValue !== null && (
          <StatDifference
            value={value}
            referenceValue={referenceValue}
            formatFn={(val) => `${Math.ceil(val)}%`}
          />
        )}
      </div>
      {showAdvanced && (
        <div className='text-sm text-gray-600'>
          {ductedCoverageTooltipText(type, { value, basis: coverage.basis })}
        </div>
      )}
    </div>
  );
}

function renderFeatureContent(
  feature: string,
  data: ComparisonTableDataType,
  comparisonTableData: ComparisonTableDataType | null,
  showAdvanced: boolean,
  mode: 'systems' | 'quotes'
) {
  switch (feature) {
    case 'Heating Coverage':
      if (data.ductedCoverage) {
        return renderDuctedCoverageContent(
          'heating',
          data,
          comparisonTableData,
          showAdvanced
        );
      }
      return data.heatingScore && data.condenserCoverage ? (
        <div className='flex flex-col items-center gap-2'>
          <div className='flex items-center gap-2'>
            <CoverageIndicator
              type='heating'
              score={data.heatingScore}
              condenserCoverage={data.condenserCoverage}
            />
            {comparisonTableData?.heatingScore &&
              !comparisonTableData.ductedCoverage &&
              comparisonTableData.identifier !== data.identifier && (
                <StatDifference
                  value={data.heatingScore}
                  referenceValue={comparisonTableData.heatingScore}
                  formatFn={formatCoverageScore}
                />
              )}
          </div>
          {showAdvanced && (
            <div className='text-sm text-gray-600'>
              {getCoverageTooltipData(
                'heating',
                data.heatingScore,
                data.condenserCoverage
              )}
            </div>
          )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Cooling Coverage':
      if (data.ductedCoverage) {
        return renderDuctedCoverageContent(
          'cooling',
          data,
          comparisonTableData,
          showAdvanced
        );
      }
      return data.coolingScore && data.condenserCoverage ? (
        <div className='flex flex-col items-center gap-2'>
          <div className='flex items-center gap-2'>
            <CoverageIndicator
              type='cooling'
              score={data.coolingScore}
              condenserCoverage={data.condenserCoverage}
            />
            {comparisonTableData?.coolingScore &&
              !comparisonTableData.ductedCoverage &&
              comparisonTableData.identifier !== data.identifier && (
                <StatDifference
                  value={data.coolingScore}
                  referenceValue={comparisonTableData.coolingScore}
                  formatFn={formatCoverageScore}
                />
              )}
          </div>
          {showAdvanced && (
            <div className='text-sm text-gray-600'>
              {getCoverageTooltipData(
                'cooling',
                data.coolingScore,
                data.condenserCoverage
              )}
            </div>
          )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Average Efficiency':
      return data.averageSeer ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{formatSeer(data.averageSeer)} SEER</span>
          {comparisonTableData?.averageSeer &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.averageSeer}
                referenceValue={comparisonTableData.averageSeer}
                formatFn={formatSeer}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Price':
      return data.price ? (
        <div className='flex items-center justify-center gap-2'>
          <span>
            {mode === 'quotes'
              ? formatUSD(data.price, true)
              : `$${data.price.toLocaleString()}`}
          </span>
          {comparisonTableData?.price &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.price}
                referenceValue={comparisonTableData.price}
                formatFn={
                  mode === 'quotes' ? (val) => formatUSD(val, true) : formatUSD
                }
                lowerIsBetter
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Total Units to Install':
      return data.totalUnitsToInstall ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{data.totalUnitsToInstall}</span>
          {comparisonTableData?.totalUnitsToInstall &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.totalUnitsToInstall}
                referenceValue={comparisonTableData.totalUnitsToInstall}
                lowerIsBetter
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Indoor Units':
      return data.totalIndoorUnits ? (
        <div className='flex flex-col items-center gap-2'>
          <div className='flex items-center gap-2'>
            <span>{data.totalIndoorUnits}</span>
            {comparisonTableData?.totalIndoorUnits &&
              comparisonTableData.identifier !== data.identifier && (
                <StatDifference
                  value={data.totalIndoorUnits}
                  referenceValue={comparisonTableData.totalIndoorUnits}
                  lowerIsBetter
                />
              )}
          </div>
          {showAdvanced && data.uniqueIndoorProducts && (
            <div className='text-left text-sm'>
              {data.uniqueIndoorProducts.map((product) => {
                const installationRequirements = product.requirements
                  .filter((req) => req.type === 'installation')
                  .map((req) => req.value);
                const drainageRequirements = product.requirements
                  .filter((req) => req.type === 'drainage')
                  .map((req) => req.value);
                const clearances = product.dimensions
                  ? [
                      product.dimensions.clearanceFront
                        ? `Front: ${product.dimensions.clearanceFront}"`
                        : null,
                      product.dimensions.clearanceSides
                        ? `Side: ${product.dimensions.clearanceSides}"`
                        : null,
                      product.dimensions.clearanceTop
                        ? `Top: ${product.dimensions.clearanceTop}"`
                        : null,
                      product.dimensions.clearanceBottom
                        ? `Bottom: ${product.dimensions.clearanceBottom}"`
                        : null
                    ].filter(Boolean)
                  : [];

                return (
                  <div key={product.productId} className='mb-2'>
                    <div className='text-lg font-semibold'>{product.name}</div>
                    {[...installationRequirements, ...drainageRequirements]
                      .length > 0 && (
                      <div className='mt-1'>
                        <strong>Requirements:</strong>
                        <ul className='ml-2 list-inside list-disc'>
                          {[
                            ...installationRequirements,
                            ...drainageRequirements
                          ].map((req) => (
                            <li key={req} className='text-sm'>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {clearances.length > 0 && (
                      <div className='mt-1'>
                        <strong>Clearances:</strong> {clearances.join(', ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Outdoor Units':
      return data.totalOutdoorUnits ? (
        <div className='flex flex-col items-center gap-2'>
          <div className='flex items-center gap-2'>
            <span>{data.totalOutdoorUnits}</span>
            {comparisonTableData?.totalOutdoorUnits &&
              comparisonTableData.identifier !== data.identifier && (
                <StatDifference
                  value={data.totalOutdoorUnits}
                  referenceValue={comparisonTableData.totalOutdoorUnits}
                  lowerIsBetter
                />
              )}
          </div>
          {showAdvanced && data.uniqueOutdoorProducts && (
            <div className='text-left text-sm'>
              {data.uniqueOutdoorProducts.map((product) => {
                const requirements = product.requirements
                  .filter(
                    (req) =>
                      req.type === 'installation' || req.type === 'drainage'
                  )
                  .map((req) => req.value);

                return (
                  <div key={product.productId} className='mb-2'>
                    <div className='text-lg font-semibold'>{product.name}</div>
                    {requirements.length > 0 && (
                      <div className='mt-1'>
                        <strong>Requirements:</strong>
                        <ul className='ml-2 list-inside list-disc'>
                          {requirements.map((req) => (
                            <li key={req} className='text-sm'>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Indoor Visibility':
      return data.indoorVisibility ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{data.indoorVisibility}</span>
          {comparisonTableData?.indoorVisibility &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.indoorVisibility}
                referenceValue={comparisonTableData.indoorVisibility}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Outdoor Visibility':
      return data.outdoorVisibility ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{data.outdoorVisibility}</span>
          {comparisonTableData?.outdoorVisibility &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.outdoorVisibility}
                referenceValue={comparisonTableData.outdoorVisibility}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Installation Difficulty':
      return data.installationDifficulty ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{data.installationDifficulty}</span>
          {comparisonTableData?.installationDifficulty &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.installationDifficulty}
                referenceValue={comparisonTableData.installationDifficulty}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Potential Rebates':
      return data.totalRebates ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{formatUSD(data.totalRebates)}*</span>
          <Tooltip text='* Products + Installation Cost' />
          {comparisonTableData &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={Math.floor(data.totalRebates)}
                referenceValue={Math.floor(
                  comparisonTableData.totalRebates ?? 0
                )}
                formatFn={formatUSD}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Price After Rebates':
      return data.price ? (
        <div className='flex items-center justify-center gap-2'>
          <span>
            {formatUSD(Math.max(data.price - (data.totalRebates ?? 0), 0))}
          </span>
          {comparisonTableData?.price &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={Math.max(data.price - (data.totalRebates ?? 0), 0)}
                referenceValue={Math.max(
                  comparisonTableData.price -
                    (comparisonTableData.totalRebates ?? 0),
                  0
                )}
                formatFn={formatUSD}
                lowerIsBetter
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    case 'Amperage':
      return data.amperage ? (
        <div className='flex items-center justify-center gap-2'>
          <span>{data.amperage}A</span>
          {comparisonTableData?.amperage &&
            comparisonTableData.identifier !== data.identifier && (
              <StatDifference
                value={data.amperage}
                referenceValue={comparisonTableData.amperage}
                lowerIsBetter
                formatFn={(val) => `${val}A`}
              />
            )}
        </div>
      ) : (
        <span className='text-gray-400'>N/A</span>
      );

    default:
      return <span className='text-gray-400'>N/A</span>;
  }
}

const UnifiedComparisonTable = forwardRef<
  HTMLDivElement,
  UnifiedComparisonTableProps
>(function UnifiedComparisonTable(
  { data, mode = 'systems', showAdvanced = false, onToggleAdvanced },
  ref
) {
  const dispatch = useAppDispatch();
  const { comparisonTableData, globalComparisonFeatures } = useAppSelector(
    (state) => state.ui
  );

  const handleItemClick = (
    e: MouseEvent,
    itemData: ComparisonTableDataType
  ) => {
    e.stopPropagation();
    if (comparisonTableData?.identifier === itemData?.identifier) {
      dispatch(setComparisonTableData(null));
    } else {
      dispatch(setComparisonTableData(itemData));
    }
  };

  if (data.length <= 1 || globalComparisonFeatures.length === 0) {
    return null;
  }

  const headerClasses = (isSelected: boolean) =>
    clsx(
      'relative w-full cursor-pointer p-4 text-center text-xl font-semibold transition-all duration-200 rounded-t-lg border-0 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      isSelected
        ? 'bg-blue-100 text-blue-900'
        : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
    );

  const cellClasses = (isSelected: boolean, isLast: boolean) =>
    clsx(
      'p-4 text-center transition-all duration-200',
      isSelected ? 'bg-blue-50' : 'bg-white',
      isLast && 'rounded-b-lg'
    );

  const itemClasses =
    'shrink-0 grow-0 basis-[calc((100%_-_2.5rem)_/_3)] snap-start';

  return (
    <div className='w-full'>
      <div
        ref={ref}
        className='hidden-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth'
      >
        {data.map((itemData, index) => {
          const isSelected =
            comparisonTableData?.identifier === itemData.identifier;

          return (
            <div key={itemData.identifier} className={itemClasses}>
              <div className='rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5'>
                <button
                  className={headerClasses(isSelected)}
                  onClick={(e) => handleItemClick(e, itemData)}
                  type='button'
                >
                  <div className='flex flex-col items-center gap-1'>
                    <span>
                      {mode === 'quotes'
                        ? `Option ${index + 1}`
                        : `System ${index + 1}`}
                    </span>
                    {isSelected ? (
                      <span className='text-sm text-blue-600'>Comparing ✓</span>
                    ) : (
                      <span className='text-sm text-gray-500'>
                        Click to compare
                      </span>
                    )}
                  </div>
                  {isSelected ? (
                    <Tooltip text='Click to Stop Comparing' />
                  ) : (
                    <Tooltip text='Click to Compare' />
                  )}
                </button>
                <div className='divide-y divide-gray-200'>
                  {globalComparisonFeatures.map((feature, featureIndex) => (
                    <div
                      key={`${feature}-${itemData.identifier}`}
                      className={cellClasses(
                        isSelected,
                        featureIndex === globalComparisonFeatures.length - 1
                      )}
                    >
                      <div className='mb-2 text-sm text-gray-500'>
                        {feature}
                      </div>
                      {renderFeatureContent(
                        feature,
                        itemData,
                        comparisonTableData,
                        showAdvanced,
                        mode
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {onToggleAdvanced && (
        <div className='mt-4 flex justify-center'>
          <Button
            color='subtle'
            text={showAdvanced ? 'Less Info' : 'More Info'}
            icon={showAdvanced ? ChevronUpIcon : ChevronDownIcon}
            iconSize='md'
            iconPosition='right'
            onClick={onToggleAdvanced}
          />
        </div>
      )}
    </div>
  );
});

export default UnifiedComparisonTable;
